import { GeoJsonLayer } from '@deck.gl/layers';
import request from '@arcgis/core/request';
import ArcgisLayer from '@arcgis/core/layers/Layer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import WMSLayer from '@arcgis/core/layers/WMSLayer';
import BaseTileLayer from '@arcgis/core/layers/BaseTileLayer';
import { Layer } from '../../../../models/layer-settings';
import { fulcrumMBTilesPNGLayer, fulcrumPNGLayer } from '../../utils/layer-urls';
import { convertWMSLayerToArcGISLayer } from '../../utils/wms-utils';
// eslint-disable-next-line no-undef
import RequestResponse = __esri.RequestResponse;
import { MANAGED_LAYER_PREFIX } from '../../arc-gis-map-view';
import buildFeatureLayer from './feature-layer-builder';
import arcGisConnection from '../../../../arcGIS/arcgis-connection';

export const tileURL = (layer: Layer, apiToken: string) => {
  switch (layer.type) {
    case 'mbtiles':
      return fulcrumMBTilesPNGLayer(layer, apiToken);
    case 'fulcrum':
    case 'data':
      return fulcrumPNGLayer(layer, apiToken);
    case 'xyz':
      return layer.url;
    default:
      return '';
  }
};

export const promiseLayers = (
  layers: Array<Promise<ArcgisLayer> | FeatureLayer | WMSLayer>,
) => Promise.all(layers.map((p) => Promise.resolve(p)));

export const markLayer = (layer: ArcgisLayer) => {
  if (!layer.id.startsWith(MANAGED_LAYER_PREFIX)) {
    layer.set('id', `${MANAGED_LAYER_PREFIX}-${layer.id}`);
  }
  return layer;
};

export const rawLayerID = (layer: ArcgisLayer) => (
  layer.id.replace(`${MANAGED_LAYER_PREFIX}-`, '')
);

export const convertLayers = (
  layers: Layer[],
  apiToken: string,
  esriApiKey: string,
) => {
  const deckLayers: any[] = [];
  const arcLayers: Array<Promise<ArcgisLayer> | FeatureLayer | WMSLayer> = [];
  layers.forEach((layer) => {
    switch (layer.type) {
      case 'feature-service':
        if (layer.origin === 'fulcrum' && layer.url) {
          const featureLayer = buildFeatureLayer(layer.url, esriApiKey);
          arcLayers.push(featureLayer);
        } else {
          arcLayers.push(PrivateArcGISOnlineLayer(layer, esriApiKey));
        }
        break;
      case 'wms': {
        arcLayers.push(convertWMSLayerToArcGISLayer(layer));
        break;
      }
      case 'mbtiles':
      case 'fulcrum':
      case 'xyz':
      case 'data':
        arcLayers.push(convertDataLayer(layer, apiToken));
        break;
      case 'geojson':
        deckLayers.push(convertGeoJsonLayer(layer));
        break;
      default:
        deckLayers.push(layer);
        break;
    }
  });
  return { deckLayers, arcLayers };
};

export const convertGeoJsonLayer = (layer) => new GeoJsonLayer({
  id: layer.id,
  data: layer.geojson ?? layer.url,
  stroked: true,
  filled: true,
  extruded: false,
  lineWidthMinPixels: 1,
  getFillColor: [160, 160, 180, 200],
  getLineColor: [0, 0, 0, 255],
  getLineWidth: 1,
});

const drawTile = (width: number, height: number, image, tint): HTMLCanvasElement => {
  // create a canvas with a filled rectangle
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = width;
  canvas.height = height;

  // Apply the color provided by the layer to the fill rectangle
  if (context) {
    if (tint) {
      context.fillStyle = tint.toCss();
      context.fillRect(0, 0, width, height);
      // apply multiply blend mode to canvas' fill color and the tile
      // returned from the server to darken the tile
      context.globalCompositeOperation = 'multiply';
    }
    context.drawImage(image, 0, 0, width, height);
  }
  return canvas;
};

let tileCache: {[url: string]: any} = {};
// @ts-ignore tsc can't find createSubclass but it exists
export const FulcrumTileLayer = BaseTileLayer.createSubclass({
  // properties of the custom tile layer
  properties: {
    urlTemplate: null,
  },

  // override getTileUrl()
  // generate the tile url for a given level, row and column
  getTileUrl(level, row, col) {
    return this.urlTemplate.replace('{z}', level).replace('{x}', col).replace('{y}', row);
  },
  // Override fetchTile - This allows us to cache tile images and not retrieve them constantly.
  // Must not be an arrow function to allow access to `this`
  fetchTile(level, row, col, options): Promise<HTMLCanvasElement | RequestResponse> {
    const self = this || { getTileUrl: () => (''), tileInfo: { size: [] }, tint: {} };
    const url = self.getTileUrl(level, row, col);
    if (url.length < 1) {
      return Promise.reject(new Error('Can\'t fetch a tile without a url'));
    }
    const width = self.tileInfo.size[0];
    const height = self.tileInfo.size[0];
    const { tint } = self;

    if (url in tileCache) {
      const canvas = drawTile(width, height, tileCache[url], tint);
      return Promise.resolve(canvas);
    }
    // request for the tile based on the url returned from getTileUrl() method.
    // the signal option ensures that obsolete requests are aborted.
    return request(url, {
      responseType: 'image',
      signal: options?.signal,
    })
      .then((response) => {
        // when esriRequest resolves successfully,
        // process the image that is returned
        const image = response.data;

        tileCache[url] = image;
        return drawTile(width, height, image, tint);
      });
  },
  // Custom fn. Need a way to empty the tile cache. This is really useful when records are
  // created/saved to trigger the layer to re-render
  clearCache() {
    tileCache = {};
  },
});

const PrivateArcGISOnlineLayer = (layer: Layer, esriApiKey: string): Promise<ArcgisLayer> => (
  Promise.resolve(null)
    .then(async () => {
      const {
        id, esriAppId, esriPortalUrl, baseURL,
      } = layer;
      const connection = arcGisConnection(esriApiKey, esriAppId, esriPortalUrl);

      return connection.fetchLayer(id, baseURL);
    })
);

export const convertDataLayer = (
  layer: Layer,
  apiToken: string,
): typeof FulcrumTileLayer => new FulcrumTileLayer({
  id: layer.id,
  urlTemplate: tileURL(layer, apiToken),
});
